import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useCallback } from "react";

import { rememberReturnPath } from "../router";
import { actingHeaders } from "./actingAs";
import { apiScope } from "./config";
import { DEV_ACTOR_HEADER, devActor } from "./identity";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

/**
 * Set once before sending someone to Entra to re-authenticate.
 *
 * Without it a browser that can never renew silently would bounce between this
 * app and the sign-in page forever, which is worse than an error message
 * because it looks like the app is broken rather than the session.
 */
const REAUTH_FLAG = "scheduler:reauthTried";

/**
 * Calls the API with the caller's identity attached — an Entra access token,
 * or in local development the dev actor header (see ./identity.ts).
 *
 * MSAL serves tokens from its cache and refreshes them silently. When silent
 * refresh CANNOT work, the only useful response is to send the person through
 * a real sign-in — and the important part is that "cannot work" covers much
 * more than the obvious case.
 *
 * Silent renewal falls back to a hidden iframe pointed at Microsoft, which
 * needs third-party cookies. Incognito windows block those by default, so the
 * iframe never completes and MSAL eventually throws `timed_out` — a
 * BrowserAuthError, NOT an InteractionRequiredAuthError. Handling only the
 * latter left the app hanging until the timeout and then failing, in exactly
 * the browsing mode used to demonstrate two people side by side.
 *
 * Fieldpath hit this first and the fix is ported from it deliberately: the two
 * apps share a tenant and are opened from each other, so a session that cannot
 * renew fails in both at once.
 */
export function useApi() {
  const { instance, accounts } = useMsal();

  return useCallback(
    /**
     * `asSelf` leaves out the acting-as headers: starting and stopping are done as the real HR
     * person, and the API refuses them from inside an acting-as session.
     */
    async (path: string, init?: RequestInit, opts: { asSelf?: boolean } = {}): Promise<Response> => {
      // While HR is acting as someone, every call carries it (auth/actingAs.ts).
      const acting = opts.asSelf ? {} : actingHeaders();
      // The literal import.meta.env.DEV check (not just devActor() returning null) lets the
      // production build drop this branch, and the header name with it.
      const actor = import.meta.env.DEV ? devActor() : null;
      if (import.meta.env.DEV && actor) {
        return fetch(`${API_BASE}${path}`, {
          ...init,
          headers: { ...init?.headers, ...acting, [DEV_ACTOR_HEADER]: actor },
        });
      }

      const account = accounts[0];
      if (!account) {
        throw new Error("Not signed in");
      }

      let token: string;
      try {
        const result = await instance.acquireTokenSilent({
          scopes: [apiScope],
          account,
        });
        token = result.accessToken;
        // A silent success means the session is healthy again.
        try {
          window.sessionStorage.removeItem(REAUTH_FLAG);
        } catch {
          // Storage unavailable; the flag is an optimisation, not a rule.
        }
      } catch (error) {
        const needsPerson =
          error instanceof InteractionRequiredAuthError || isSilentRenewalFailure(error);

        if (!needsPerson) throw error;

        let alreadyTried = false;
        try {
          alreadyTried = window.sessionStorage.getItem(REAUTH_FLAG) === "1";
          window.sessionStorage.setItem(REAUTH_FLAG, "1");
        } catch {
          // Storage unavailable. One redirect is still better than none.
        }

        if (alreadyTried) {
          throw new Error(
            "Your sign-in could not be renewed. Please reload the page and sign in again.",
          );
        }

        // Redirect rather than popup: popups are blocked by default in many
        // browsers, and a blocked popup fails SILENTLY, which is the same dead
        // end by another route. The path is remembered first so the person
        // comes back to the screen they were on rather than the default one.
        rememberReturnPath();
        await instance.acquireTokenRedirect({ scopes: [apiScope], account });

        // acquireTokenRedirect navigates away; nothing after it runs.
        throw new Error("Redirecting to sign in…");
      }

      return fetch(`${API_BASE}${path}`, {
        ...init,
        headers: {
          ...init?.headers,
          ...acting,
          Authorization: `Bearer ${token}`,
        },
      });
    },
    [instance, accounts],
  );
}

/**
 * Whether a silent-token failure is one a real sign-in would fix.
 *
 * Matched on the error CODE rather than the class, because MSAL reports these
 * as BrowserAuthError — the same class it uses for genuine programming
 * mistakes, which must keep surfacing rather than triggering a redirect loop.
 */
function isSilentRenewalFailure(error: unknown): boolean {
  const code =
    typeof error === "object" && error !== null && "errorCode" in error
      ? String((error as { errorCode: unknown }).errorCode)
      : "";

  return (
    code === "timed_out" ||
    code === "monitor_window_timeout" ||
    code === "iframe_closed_prematurely" ||
    code === "silent_prompt_value_error" ||
    code === "no_account_error" ||
    code === "no_tokens_found"
  );
}
